import { Box, CircularProgress, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { Delivery } from "@types_def/models/delivery.types";
import { DeliveryStatus } from "@types_def/models/delivery.types";
import { useCallback, useState } from "react";

import { updateBySMS } from "@/api/Rest/confirm-delivery-by-sms";
import { useIdToken } from "@/hooks/idToken";
import { graphqlClient } from "@/libs/graphql-fetcher";

import AddressSection from "./Address";
import Buttons from "./Buttons";
import Cancel from "./Cancel";
import ContactInformation from "./ContactInformation";
import DateTimeSection from "./DateTime";
import IncorrectLink from "./IncorrectLink";
import Options from "./Options";
import PostConfirmation from "./PostConfimation";
import { InformationTabe } from "./StyledComponents";
import Vehicles from "./Vehicles";
import Welcome from "./Welcome";

export const ConfirmationContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: "100%",
  height: "100%",
  color: "white",
  padding: "5em",
  [theme.breakpoints.down("sm")]: {
    padding: "1em",
  },
}));

export const ConfirmationContent = styled(Box)({
  margin: "auto",
});

enum DeliveryFeedBack {
  LOADING,
  CONFIRMED,
  CANCELED,
  PENDING,
  INCORRECT,
}

function DeliveryContent({ delivery }: { delivery: Delivery }) {
  const [feedback, setFeedback] = useState<DeliveryFeedBack>(() => {
    if (delivery.status !== DeliveryStatus.PENDING_CUSTOMER_CONFIRMATION) {
      return DeliveryFeedBack.INCORRECT;
    }
    return DeliveryFeedBack.PENDING;
  });
  const { token } = useIdToken();

  const updateDeliveryStatusBySMS = useCallback(
    (status: DeliveryStatus) => {
      if (token == null) {
        return;
      }

      setFeedback(DeliveryFeedBack.LOADING);
      void updateBySMS(token, status)
        .then((updatedDelivery) => {
          if (updatedDelivery == null) {
            return;
          }

          setFeedback(
            status === DeliveryStatus.CONFIRMED_BY_CUSTOMER
              ? DeliveryFeedBack.CONFIRMED
              : DeliveryFeedBack.CANCELED
          );
        })
        .catch((_error: unknown): void => {
          // Error already handled by updateBySMS
          setFeedback(DeliveryFeedBack.PENDING);
        });
    },
    [token]
  );

  return (
    <ConfirmationContainer>
      <ConfirmationContent>
        <img style={{ margin: "auto" }} src="/images/logo.svg" />
        <br></br>
        {feedback === DeliveryFeedBack.INCORRECT && <IncorrectLink />}
        {feedback === DeliveryFeedBack.LOADING && <CircularProgress color="secondary" />}
        {feedback === DeliveryFeedBack.CONFIRMED && <PostConfirmation />}
        {feedback === DeliveryFeedBack.CANCELED && <Cancel />}
        {feedback === DeliveryFeedBack.PENDING && (
          <>
            <Welcome clientFirstName={delivery.clientFirstName} />
            <InformationTabe>
              {delivery.carPickUpDate != null ? (
                <DateTimeSection carPickUpDate={delivery.carPickUpDate} />
              ) : null}

              {delivery.toAddress?.address != null ? (
                <AddressSection address={delivery.toAddress.address} />
              ) : null}

              <Vehicles vehicles={delivery.vehicles} />
              <Options selectedOptions={delivery.selectedOptions} />
              <ContactInformation email={delivery.clientEmail} phone={delivery.clientPhone} />
            </InformationTabe>

            <Buttons updateDeliveryStatusBySMS={updateDeliveryStatusBySMS} />
          </>
        )}
      </ConfirmationContent>
    </ConfirmationContainer>
  );
}

export function AcceptDeliveryByClient() {
  const { id } = useIdToken();
  const { data: delivery, isLoading } = useQuery({
    queryKey: ["public-delivery-confirmation", id],
    enabled: id != null,
    queryFn: async () => {
      const response = await graphqlClient.request<{ findDeliveryById: Delivery }>(
        `
          query findDeliveryById($GetByIdInput: GetByIdInput!) {
            findDeliveryById(GetByIdInput: $GetByIdInput) {
              id
              twoWayDeliverType
              type
              status
              clientFirstName
              clientPhone
              clientEmail
              selectedOptions {
                id
                name
                clientPrice
                photo
              }
              carAvailabilityDate
              carPickUpDate
              carDeliveryDeadline
              vehicles {
                marque
                model
                toRetrieve
                vehicleCode
              }
              fromAddress {
                postalCode
                city
                address
              }
              toAddress {
                postalCode
                city
                address
              }
            }
          }
        `,
        { GetByIdInput: { id: Number(id) } }
      );

      return response.findDeliveryById;
    },
  });

  if (isLoading) {
    return (
      <ConfirmationContainer>
        <ConfirmationContent>
          <CircularProgress color="secondary" />
        </ConfirmationContent>
      </ConfirmationContainer>
    );
  }

  if (!delivery) {
    return null;
  }

  return <DeliveryContent delivery={delivery} />;
}
